// Reusable kiosk PIN control.
//
// Adapts to whether a PIN is already set (queried on mount via the staff
// pin-status endpoint):
//   - no PIN  → "Set PIN" flow:    New PIN + Confirm PIN.
//   - has PIN → "Change PIN" flow: Current PIN + New PIN + Confirm PIN,
//                                  plus a "Remove PIN" action (also gated on
//                                  the current PIN).
//
// Each PIN is four individual digit boxes (auto-advance on input, backspace
// moves back). Used by the staff profile page and the kiosk settings screen
// so the entry pattern is identical in both places.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { getKioskPinStatus, removeKioskPin, setKioskPin } from '../../../api/kiosk';
import styles from './KioskPinSetup.module.css';

export interface KioskPinSetupProps {
  /** Override the set/replace call (tests / custom flows). Defaults to api/kiosk. */
  readonly onSet?: (pin: string, currentPin?: string) => Promise<void>;
  /** Override the remove call (tests / custom flows). Defaults to api/kiosk. */
  readonly onRemove?: (currentPin: string) => Promise<void>;
  /** Override the has-PIN lookup (tests). Defaults to api/kiosk. */
  readonly getStatus?: () => Promise<boolean>;
}

function emptyDigits(): string[] {
  return ['', '', '', ''];
}

/** A single labelled four-box PIN entry with auto-advance + backspace nav. */
function PinRow({
  label,
  digits,
  onChange,
}: {
  label: string;
  digits: string[];
  onChange: (digits: string[]) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const setDigit = (i: number, raw: string) => {
    const v = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    onChange(next);
    if (v && i < 3) refs.current[i + 1]?.focus();
  };

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && digits[i] === '' && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  return (
    <div className={styles.field}>
      <span className={styles.rowLabel}>{label}</span>
      <div className={styles.boxes}>
        {[0, 1, 2, 3].map((i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={styles.box}
            inputMode="numeric"
            type="password"
            maxLength={1}
            value={digits[i]}
            aria-label={`${label} digit ${i + 1}`}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
          />
        ))}
      </div>
    </div>
  );
}

const FOUR_DIGITS = /^\d{4}$/;

export function KioskPinSetup({ onSet, onRemove, getStatus }: KioskPinSetupProps) {
  const [hasPin, setHasPin] = useState<boolean | null>(null);
  const [current, setCurrent] = useState<string[]>(emptyDigits);
  const [next, setNext] = useState<string[]>(emptyDigits);
  const [confirm, setConfirm] = useState<string[]>(emptyDigits);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    let active = true;
    void (getStatus ?? getKioskPinStatus)()
      .then((v) => {
        if (active) setHasPin(v);
      })
      .catch(() => {
        // Default to the (safer) set flow if the status lookup fails.
        if (active) setHasPin(false);
      });
    return () => {
      active = false;
    };
  }, [getStatus]);

  const currentPin = current.join('');
  const nextPin = next.join('');
  const confirmPin = confirm.join('');
  const currentComplete = FOUR_DIGITS.test(currentPin);
  const nextComplete = FOUR_DIGITS.test(nextPin);
  const confirmComplete = FOUR_DIGITS.test(confirmPin);

  const clearAll = () => {
    setCurrent(emptyDigits());
    setNext(emptyDigits());
    setConfirm(emptyDigits());
  };

  const errorText = (err: unknown, fallback: string): string => {
    const status = (err as { response?: { status?: number } }).response?.status;
    return status === 401 ? 'Current PIN is incorrect.' : fallback;
  };

  const canSubmit =
    nextComplete && confirmComplete && (hasPin ? currentComplete : true) && !busy;

  const doSubmit = async () => {
    if (!nextComplete || !confirmComplete) return;
    if (nextPin !== confirmPin) {
      setFeedback({ kind: 'err', text: 'New PINs do not match.' });
      return;
    }
    if (hasPin && !currentComplete) return;
    setBusy(true);
    setFeedback(null);
    try {
      await (onSet ?? setKioskPin)(nextPin, hasPin ? currentPin : undefined);
      setFeedback({ kind: 'ok', text: 'PIN saved.' });
      clearAll();
      setHasPin(true);
    } catch (err) {
      setFeedback({ kind: 'err', text: errorText(err, 'Could not save PIN.') });
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async () => {
    if (!currentComplete) {
      setFeedback({ kind: 'err', text: 'Enter your current PIN to remove it.' });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      await (onRemove ?? removeKioskPin)(currentPin);
      setFeedback({ kind: 'ok', text: 'PIN removed.' });
      clearAll();
      setHasPin(false);
    } catch (err) {
      setFeedback({ kind: 'err', text: errorText(err, 'Could not remove PIN.') });
    } finally {
      setBusy(false);
    }
  };

  if (hasPin === null) {
    return <p className={styles.help}>Loading…</p>;
  }

  return (
    <div className={styles.wrap}>
      <p className={styles.help}>
        {hasPin
          ? 'Change or remove your 4-digit kiosk PIN.'
          : 'Set a 4-digit PIN to log in to the weighbridge kiosk quickly.'}
      </p>

      {hasPin ? (
        <PinRow label="Current PIN" digits={current} onChange={setCurrent} />
      ) : null}
      <PinRow label="New PIN" digits={next} onChange={setNext} />
      <PinRow label="Confirm PIN" digits={confirm} onChange={setConfirm} />

      {feedback ? (
        <p className={feedback.kind === 'ok' ? styles.ok : styles.err} role="status">
          {feedback.text}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.setBtn}
          onClick={() => void doSubmit()}
          disabled={!canSubmit}
        >
          {hasPin ? 'Update PIN' : 'Set PIN'}
        </button>
        {hasPin ? (
          <button
            type="button"
            className={styles.removeBtn}
            onClick={() => void doRemove()}
            disabled={busy || !currentComplete}
          >
            Remove PIN
          </button>
        ) : null}
      </div>
    </div>
  );
}
